import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCategories,
  useUpdateTransaction,
  useAddKeyword,
  useUpdateKeyword,
  useDeleteTransaction,
} from "../hooks/useApi";
import { api } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import AddTransactionDialog from "../components/AddTransactionDialog";
import ConfirmationDialog from "../components/ConfirmationDialog";
import { useToast } from "./Toast";
import { RESPONSIBLE_PEOPLE, UNASSIGNED_PERSON, normalizePerson } from "../utils/people";
import { findBestMatchingKeyword } from "../utils/keywordMatch";

const PERSON_BADGE = {
  Nora: "bg-pink-100 text-pink-800",
  Barry: "bg-blue-100 text-blue-800",
  Ben: "bg-emerald-100 text-emerald-800",
  Jacob: "bg-amber-100 text-amber-800",
  Marcus: "bg-violet-100 text-violet-800",
  Household: "bg-slate-200 text-slate-800",
};

const PersonCell = ({ person, isEditing, onEdit, onChange, onCancel }) => {
  const assigned = normalizePerson(person);

  if (isEditing) {
    return (
      <select
        autoFocus
        value={assigned || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onCancel}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-purple-500 focus:border-purple-500"
      >
        <option value="">Unassigned</option>
        {RESPONSIBLE_PEOPLE.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
    );
  }

  return (
    <span
      onClick={onEdit}
      className={`cursor-pointer text-xs font-semibold px-2 py-1 rounded hover:opacity-80 ${
        assigned ? PERSON_BADGE[assigned] : "bg-gray-100 text-gray-500"
      }`}
    >
      {assigned || "Unassigned"}
    </span>
  );
};

PersonCell.propTypes = {
  person: PropTypes.string,
  isEditing: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const TransactionsTab = ({
  transactions,
  spendByCategory,
  formatDate,
  formatCurrency,
  onTransactionsUpdate,
  budgetComparison = [], // Add budget comparison data
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSubCategory, setFilterSubCategory] = useState("");
  const [filterPerson, setFilterPerson] = useState("");
  const [editingCell, setEditingCell] = useState(null);
  const [localTransactions, setLocalTransactions] = useState(transactions);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  // React Query hooks
  const { data: availableCategories = [] } = useCategories();
  const updateTransactionMutation = useUpdateTransaction();
  const addKeywordMutation = useAddKeyword();
  const updateKeywordMutation = useUpdateKeyword();
  const deleteTransactionMutation = useDeleteTransaction();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const transactionCountRef = useRef(transactions.length);
  const itemsPerPage = 10;

  // Helper function to determine if a transaction category is budgeted
  const isTransactionBudgeted = (transaction) => {
    if (!budgetComparison || budgetComparison.length === 0) return false;
    
    const transactionCategory = transaction.final_category?.toLowerCase();
    if (!transactionCategory) return false;
    
    return budgetComparison.some(budget => {
      const budgetCategory = budget.category.toLowerCase();
      return budgetCategory === transactionCategory || 
             budgetCategory.includes(transactionCategory) || 
             transactionCategory.includes(budgetCategory);
    });
  };

  // Pagination logic for transactions
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // Filter transactions by category and sub-category
  const filteredTransactions = localTransactions.filter((transaction) => {
    const categoryMatch = filterCategory
      ? transaction.final_category
          .toLowerCase()
          .includes(filterCategory.toLowerCase())
      : true;

    const subCategoryMatch = filterSubCategory
      ? (transaction.sub_category || "")
          .toLowerCase()
          .includes(filterSubCategory.toLowerCase())
      : true;

    const assignedPerson = normalizePerson(transaction.responsible_person);
    const personMatch = !filterPerson
      ? true
      : filterPerson === UNASSIGNED_PERSON
        ? !assignedPerson
        : assignedPerson === filterPerson;

    return categoryMatch && subCategoryMatch && personMatch;
  });

  // Update currentTransactions to use filteredTransactions
  const currentTransactions = filteredTransactions.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Replace the list when rows are added or removed. A field edit keeps the
  // row that was just updated instead of swapping the whole table.
  useEffect(() => {
    setLocalTransactions((current) => {
      const sameRows =
        current.length > 0 &&
        current.length === transactions.length &&
        current.every((transaction, index) => transaction.id === transactions[index]?.id);
      return sameRows ? current : transactions;
    });
    if (transactions.length !== transactionCountRef.current) {
      setCurrentPage(1);
      transactionCountRef.current = transactions.length;
    }
  }, [transactions]);

  // Categories are loaded via React Query hook

  const syncKeywordFromTransaction = async (
    transaction,
    { updateCategory = false, updatePerson = false } = {},
  ) => {
    const description = transaction.description || "";
    const category = transaction.final_category;
    const person = normalizePerson(transaction.responsible_person);
    if (!description) return;
    if (updateCategory && (!category || category === "Uncategorized")) return;

    try {
      const cachedKeywords = queryClient.getQueryData(queryKeys.keywords.all);
      const keywords = Array.isArray(cachedKeywords)
        ? cachedKeywords
        : await api.getKeywords();
      const match = findBestMatchingKeyword(description, keywords);

      if (match) {
        await updateKeywordMutation.mutateAsync({
          id: match.id,
          keyword: match.keyword,
          category: updateCategory ? category : match.category,
          person: updatePerson ? person : match.person || null,
        });
        showToast(
          `Updated keyword "${match.keyword}" for future transactions`,
          "success",
        );
        return;
      }

      const categoryForNew = category;
      if (!categoryForNew || categoryForNew === "Uncategorized") return;

      const keyword = description.toLowerCase().trim();
      await addKeywordMutation.mutateAsync({
        keyword,
        category: categoryForNew,
        person,
      });
      showToast(`Added keyword "${keyword}" for future transactions`, "success");
    } catch (keywordError) {
      console.warn("Transaction saved, but the keyword was not updated", keywordError);
      showToast("Transaction saved, but the keyword was not updated", "error");
    }
  };

  const saveTransactionEdit = async (transactionIndex, patch, keywordOptions) => {
    if (
      transactionIndex < 0 ||
      transactionIndex >= localTransactions.length
    ) {
      setEditingCell(null);
      return;
    }

    const previous = localTransactions;
    const updated = { ...localTransactions[transactionIndex], ...patch };
    const next = localTransactions.map((transaction, index) =>
      index === transactionIndex ? updated : transaction,
    );

    setLocalTransactions(next);
    setEditingCell(null);

    try {
      await updateTransactionMutation.mutateAsync(updated);
      if (onTransactionsUpdate) {
        onTransactionsUpdate(next);
      }
      if (keywordOptions) {
        void syncKeywordFromTransaction(updated, keywordOptions);
      }
    } catch (error) {
      console.error("Error updating transaction:", error);
      setLocalTransactions(previous);
      showToast("Couldn't save that change. Please try again.", "error");
    }
  };

  const handleSubCategoryEdit = (transactionIndex, newSubCategory) =>
    saveTransactionEdit(transactionIndex, {
      sub_category: newSubCategory || null,
    });

  const handleCategoryEdit = (transactionIndex, newCategory) =>
    saveTransactionEdit(
      transactionIndex,
      { final_category: newCategory || "Uncategorized" },
      newCategory && newCategory !== "Uncategorized"
        ? { updateCategory: true }
        : null,
    );

  const handlePersonEdit = (transactionIndex, newPerson) =>
    saveTransactionEdit(
      transactionIndex,
      { responsible_person: newPerson || null },
      { updatePerson: true },
    );

  const handleCategoryAssignment = (transactionIndex, newCategory) => {
    if (!newCategory || newCategory === "Uncategorized") return;
    const current = localTransactions[transactionIndex];
    return saveTransactionEdit(
      transactionIndex,
      { final_category: newCategory },
      {
        updateCategory: true,
        updatePerson: Boolean(normalizePerson(current?.responsible_person)),
      },
    );
  };

  // Get unique sub-categories for suggestions
  const getUniqueSubCategories = () => {
    const subCategories = localTransactions
      .map((t) => t.sub_category)
      .filter(Boolean); // Remove null/undefined values

    // Use Set to ensure uniqueness, then convert back to array
    const uniqueSubCategories = [...new Set(subCategories)];

    // Filter out the predefined options to avoid duplicates (case-insensitive comparison)
    const predefinedOptions = ["business", "taxable", "special", "planned"];
    const filteredSubCategories = uniqueSubCategories.filter(
      (subCat) => !predefinedOptions.includes(subCat.toLowerCase()),
    );

    return filteredSubCategories.sort();
  };

  // Handle delete transaction
  const handleDeleteClick = (transaction) => {
    setTransactionToDelete(transaction);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!transactionToDelete) return;

    const removedId = transactionToDelete.id;
    const previous = localTransactions;
    const updatedTransactions = localTransactions.filter(
      (transaction) => transaction.id !== removedId,
    );
    setLocalTransactions(updatedTransactions);
    setShowDeleteDialog(false);
    setTransactionToDelete(null);
    if (onTransactionsUpdate) {
      onTransactionsUpdate(updatedTransactions);
    }

    try {
      await deleteTransactionMutation.mutateAsync(removedId);
    } catch (error) {
      console.error("Error deleting transaction:", error);
      setLocalTransactions(previous);
      showToast("Couldn't delete that transaction. Please try again.", "error");
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setTransactionToDelete(null);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-700 mb-4">
        Recent Transactions
      </h2>
      <button
        type="button"
        onClick={() => setShowAddDialog(true)}
        className="mb-4 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
      >
        Add Transaction
      </button>
      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label
              htmlFor="filterCategory"
              className="block text-sm font-medium text-gray-700"
            >
              Filter by Category
            </label>
            <input
              type="text"
              id="filterCategory"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              placeholder="Enter category to filter"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="filterSubCategory"
              className="block text-sm font-medium text-gray-700"
            >
              Filter by Sub-Category
            </label>
            <input
              type="text"
              id="filterSubCategory"
              value={filterSubCategory}
              onChange={(e) => setFilterSubCategory(e.target.value)}
              placeholder="Enter sub-category to filter"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="filterPerson"
              className="block text-sm font-medium text-gray-700"
            >
              Filter by Person
            </label>
            <select
              id="filterPerson"
              value={filterPerson}
              onChange={(e) => setFilterPerson(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            >
              <option value="">All</option>
              {RESPONSIBLE_PEOPLE.map((person) => (
                <option key={person} value={person}>
                  {person}
                </option>
              ))}
              <option value={UNASSIGNED_PERSON}>{UNASSIGNED_PERSON}</option>
            </select>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-gray-100 text-gray-600 uppercase text-xs sm:text-sm leading-normal">
              <th className="py-2 px-2 sm:py-3 sm:px-4 text-left">Date</th>
              <th className="py-2 px-2 sm:py-3 sm:px-4 text-left">
                Description
              </th>
              <th className="py-2 px-2 sm:py-3 sm:px-4 text-left">Category</th>
              <th className="py-2 px-2 sm:py-3 sm:px-4 text-left">
                Sub-Category
              </th>
              <th className="py-2 px-2 sm:py-3 sm:px-4 text-left">Person</th>
              <th className="py-2 px-2 sm:py-3 sm:px-4 text-center" title="Budgeted">
                💰
              </th>
              <th className="py-2 px-2 sm:py-3 sm:px-4 text-right">Amount</th>
              <th className="py-2 px-2 sm:py-3 sm:px-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 text-xs sm:text-sm">
            {currentTransactions.map((transaction, index) => {
              // For editing, we need to find the actual index in localTransactions
              // The absoluteIndex in filteredTransactions
              const filteredIndex = indexOfFirstItem + index;

              // Find this transaction's index in the original localTransactions array
              // We'll search for matches and use the filteredIndex to pick the right one for duplicates
              const matchingIndices = localTransactions
                .map((t, idx) => {
                  const matches =
                    t.date === transaction.date &&
                    t.description === transaction.description &&
                    t.amount === transaction.amount &&
                    t.final_category === transaction.final_category;
                  return matches ? idx : -1;
                })
                .filter((idx) => idx !== -1);

              // For duplicate transactions, try to pick the right instance
              // Use modulo to cycle through duplicates based on position
              const actualIndex =
                matchingIndices.length > 0
                  ? matchingIndices[filteredIndex % matchingIndices.length]
                  : -1;

              // Create a unique key using the filtered index to avoid React key conflicts
              const uniqueKey = `filtered-${filteredIndex}`;

              return (
                <tr
                  key={uniqueKey}
                  className="border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="py-2 px-2 sm:py-3 sm:px-4">
                    {formatDate(transaction.date)}
                  </td>
                  <td className="py-2 px-2 sm:py-3 sm:px-4">
                    <div className="truncate max-w-[150px] sm:max-w-[200px]">
                      {transaction.description}
                    </div>
                  </td>
                  <td className="py-2 px-2 sm:py-3 sm:px-4">
                    {editingCell === `${actualIndex}-final_category` ? (
                      <select
                        autoFocus
                        value={transaction.final_category || ""}
                        onChange={(e) =>
                          handleCategoryEdit(actualIndex, e.target.value)
                        }
                        onBlur={() => setEditingCell(null)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Select Category...</option>
                        {availableCategories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        onClick={() =>
                          setEditingCell(`${actualIndex}-final_category`)
                        }
                        className="cursor-pointer bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded hover:bg-purple-200"
                      >
                        {transaction.final_category}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 sm:py-3 sm:px-4">
                    {editingCell === `${actualIndex}-sub_category` ? (
                      <select
                        autoFocus
                        value={transaction.sub_category || ""}
                        onChange={(e) =>
                          handleSubCategoryEdit(actualIndex, e.target.value)
                        }
                        onBlur={() => setEditingCell(null)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">None</option>
                        <option value="Business">Business</option>
                        <option value="Taxable">Taxable</option>
                        <option value="Special">Special</option>
                        <option value="Planned">Planned</option>
                        {getUniqueSubCategories().map((subCat) => (
                          <option key={subCat} value={subCat}>
                            {subCat}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        onClick={() =>
                          setEditingCell(`${actualIndex}-sub_category`)
                        }
                        className={`cursor-pointer px-2 py-1 text-xs rounded ${
                          transaction.sub_category
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-500"
                        } hover:bg-gray-200`}
                      >
                        {transaction.sub_category || "Click to edit"}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 sm:py-3 sm:px-4">
                    <PersonCell
                      person={transaction.responsible_person}
                      isEditing={editingCell === `${actualIndex}-responsible_person`}
                      onEdit={() =>
                        setEditingCell(`${actualIndex}-responsible_person`)
                      }
                      onChange={(value) => handlePersonEdit(actualIndex, value)}
                      onCancel={() => setEditingCell(null)}
                    />
                  </td>
                  <td className="py-2 px-2 sm:py-3 sm:px-4 text-center">
                    {isTransactionBudgeted(transaction) ? (
                      <span 
                        className="text-green-600" 
                        title="This expense category is budgeted"
                      >
                        ✓
                      </span>
                    ) : (
                      <span 
                        className="text-gray-400" 
                        title="This expense is not budgeted"
                      >
                        ○
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 sm:py-3 sm:px-4 text-right font-medium">
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td className="py-2 px-2 sm:py-3 sm:px-4 text-center">
                    <button
                      type="button"
                      onClick={() => handleDeleteClick(transaction)}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded-full transition-colors"
                      title="Delete transaction"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4">
        <div className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-0">
          Showing {indexOfFirstItem + 1} to{" "}
          {Math.min(indexOfLastItem, filteredTransactions.length)} of{" "}
          {filteredTransactions.length} transactions
        </div>
        <div className="flex flex-wrap justify-center">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-2 py-1 mx-1 rounded ${
              currentPage === 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-purple-100 text-purple-700 hover:bg-purple-200"
            }`}
          >
            Prev
          </button>
          {[...Array(Math.min(5, totalPages))].map((_, i) => {
            // Show page numbers around current page
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => paginate(pageNum)}
                className={`px-2 py-1 mx-1 rounded ${
                  currentPage === pageNum
                    ? "bg-purple-600 text-white"
                    : "bg-purple-100 text-purple-700 hover:bg-purple-200"
                }`}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-2 py-1 mx-1 rounded ${
              currentPage === totalPages
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-purple-100 text-purple-700 hover:bg-purple-200"
            }`}
          >
            Next
          </button>
        </div>
      </div>
      {showAddDialog && (
        <AddTransactionDialog
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
        />
      )}

      {showDeleteDialog && transactionToDelete && (
        <ConfirmationDialog
          isOpen={showDeleteDialog}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Transaction"
          message={`Are you sure you want to delete this transaction? "${transactionToDelete.description}" for ${formatCurrency(transactionToDelete.amount)} on ${formatDate(transactionToDelete.date)}. This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={deleteTransactionMutation.isPending}
        />
      )}

      {spendByCategory.some(
        (category) => category.name === "Uncategorized",
      ) && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            Uncategorized Transactions
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-gray-100 text-gray-600 uppercase text-xs sm:text-sm leading-normal">
                  <th className="py-2 px-2 sm:py-3 sm:px-4 text-left">Date</th>
                  <th className="py-2 px-2 sm:py-3 sm:px-4 text-left">
                    Description
                  </th>
                  <th className="py-2 px-2 sm:py-3 sm:px-4 text-left">
                    Category
                  </th>
                  <th className="py-2 px-2 sm:py-3 sm:px-4 text-left">
                    Sub-Category
                  </th>
                  <th className="py-2 px-2 sm:py-3 sm:px-4 text-left">Person</th>
                  <th className="py-2 px-2 sm:py-3 sm:px-4 text-center" title="Budgeted">
                    💰
                  </th>
                  <th className="py-2 px-2 sm:py-3 sm:px-4 text-right">
                    Amount
                  </th>
                  <th className="py-2 px-2 sm:py-3 sm:px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="text-gray-600 text-xs sm:text-sm">
                {localTransactions
                  .map((transaction, originalIndex) => ({
                    transaction,
                    originalIndex,
                  }))
                  .filter(
                    ({ transaction }) =>
                      transaction.final_category === "Uncategorized",
                  )
                  .map(({ transaction, originalIndex }, index) => {
                    return (
                      <tr
                        key={`uncategorized-${index}`}
                        className="border-b border-gray-200 hover:bg-gray-50"
                      >
                        <td className="py-2 px-2 sm:py-3 sm:px-4">
                          {formatDate(transaction.date)}
                        </td>
                        <td className="py-2 px-2 sm:py-3 sm:px-4">
                          <div className="truncate max-w-[150px] sm:max-w-[200px]">
                            {transaction.description}
                          </div>
                        </td>
                        <td className="py-2 px-2 sm:py-3 sm:px-4">
                          <select
                            value={
                              transaction.final_category || "Uncategorized"
                            }
                            onChange={(e) => {
                              if (
                                originalIndex !== -1 &&
                                e.target.value !== "Uncategorized"
                              ) {
                                handleCategoryAssignment(
                                  originalIndex,
                                  e.target.value,
                                  transaction.description,
                                );
                              }
                            }}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                          >
                            <option value="Uncategorized">
                              Select Category...
                            </option>
                            {availableCategories.map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-2 sm:py-3 sm:px-4">
                          {editingCell ===
                          `uncategorized-${originalIndex}-sub_category` ? (
                            <select
                              autoFocus
                              value={transaction.sub_category || ""}
                              onChange={(e) => {
                                if (originalIndex !== -1) {
                                  handleSubCategoryEdit(
                                    originalIndex,
                                    e.target.value,
                                  );
                                }
                              }}
                              onBlur={() => setEditingCell(null)}
                              onKeyDown={(e) => {
                                if (e.key === "Escape") {
                                  setEditingCell(null);
                                }
                              }}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                            >
                              <option value="">None</option>
                              <option value="Business">Business</option>
                              <option value="Taxable">Taxable</option>
                              <option value="Special">Special</option>
                              {getUniqueSubCategories().map((subCat) => (
                                <option key={subCat} value={subCat}>
                                  {subCat}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span
                              onClick={() =>
                                setEditingCell(
                                  `uncategorized-${originalIndex}-sub_category`,
                                )
                              }
                              className={`cursor-pointer px-2 py-1 text-xs rounded ${
                                transaction.sub_category
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-500"
                              } hover:bg-gray-200`}
                            >
                              {transaction.sub_category || "Click to edit"}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-2 sm:py-3 sm:px-4">
                          <PersonCell
                            person={transaction.responsible_person}
                            isEditing={
                              editingCell ===
                              `uncategorized-${originalIndex}-responsible_person`
                            }
                            onEdit={() =>
                              setEditingCell(
                                `uncategorized-${originalIndex}-responsible_person`,
                              )
                            }
                            onChange={(value) => {
                              if (originalIndex !== -1) {
                                handlePersonEdit(originalIndex, value);
                              }
                            }}
                            onCancel={() => setEditingCell(null)}
                          />
                        </td>
                        <td className="py-2 px-2 sm:py-3 sm:px-4 text-center">
                          {isTransactionBudgeted(transaction) ? (
                            <span 
                              className="text-green-600" 
                              title="This expense category is budgeted"
                            >
                              ✓
                            </span>
                          ) : (
                            <span 
                              className="text-gray-400" 
                              title="This expense is not budgeted"
                            >
                              ○
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-2 sm:py-3 sm:px-4 text-right font-medium">
                          {formatCurrency(transaction.amount)}
                        </td>
                        <td className="py-2 px-2 sm:py-3 sm:px-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(transaction)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded-full transition-colors"
                            title="Delete transaction"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

TransactionsTab.propTypes = {
  transactions: PropTypes.array.isRequired,
  spendByCategory: PropTypes.array.isRequired,
  formatDate: PropTypes.func.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  onTransactionsUpdate: PropTypes.func.isRequired,
  budgetComparison: PropTypes.array,
};

export default TransactionsTab;
